import { useState } from 'react'
import { Head } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import DateRangeFilter, { DateRange, getDateLabel } from '@/components/DateRangeFilter'
import { DollarSign, TrendingUp, PieChart } from 'lucide-react'

interface MyCommission {
  id: number
  carrier_name: string
  policy_number: string
  commission_type: string
  gross_amount: number
  agent_amount: number
  agent_split_percentage: number | null
  sponsor_amount: number | null
  sponsor_split_percentage: number | null
  parent_agent_name: string | null
  status: string
  period_start: string
  period_end: string
  created_at: string
}

interface OverrideCommission {
  id: number
  agent_name: string
  carrier_name: string
  policy_number: string
  gross_amount: number
  override_amount: number
  override_percentage: number | null
  status: string
  period_start: string
  period_end: string
  created_at: string
}

interface Totals {
  direct: number
  overrides: number
  combined: number
  commission_count: number
}

interface Props extends PageProps {
  my_commissions: MyCommission[]
  override_commissions: OverrideCommission[]
  totals: Totals
  date_range: DateRange
}

export default function MyCommissionsIndex({ my_commissions, override_commissions, totals, date_range }: Props) {
  const [activeTab, setActiveTab] = useState<'direct' | 'overrides'>('direct')

  const formatCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700'
  }

  const periodLabel = date_range.preset === 'all_time' ? '' : ` — ${getDateLabel(date_range)}`

  const directColumns: Column<MyCommission>[] = [
    {
      key: 'carrier_name',
      header: 'Carrier',
      sortable: true
    },
    {
      key: 'policy_number',
      header: 'Policy #',
      sortable: true,
      render: (c) => <span className="font-mono text-sm">{c.policy_number}</span>
    },
    {
      key: 'gross_amount',
      header: 'Gross',
      sortable: true,
      className: 'text-right',
      render: (c) => formatCurrency(c.gross_amount)
    },
    {
      key: 'agent_amount',
      header: 'My Share',
      sortable: true,
      className: 'text-right',
      render: (c) => (
        <div>
          <div className="font-semibold text-chestnut-forest">{formatCurrency(c.agent_amount)}</div>
          {c.agent_split_percentage && (
            <div className="text-xs text-chestnut-charcoal/60">{c.agent_split_percentage}%</div>
          )}
        </div>
      )
    },
    {
      key: 'sponsor_amount',
      header: 'Sponsor Share',
      sortable: true,
      className: 'text-right',
      render: (c) => (
        <div>
          <div className="text-chestnut-gold">{formatCurrency(c.sponsor_amount || 0)}</div>
          {c.sponsor_split_percentage && (
            <div className="text-xs text-chestnut-charcoal/60">{c.sponsor_split_percentage}% to {c.parent_agent_name || 'N/A'}</div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[c.status]}`}>
          {c.status}
        </span>
      )
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (c) => new Date(c.created_at).toLocaleDateString()
    }
  ]

  const overrideColumns: Column<OverrideCommission>[] = [
    {
      key: 'agent_name',
      header: 'From Agent',
      sortable: true,
      render: (c) => <span className="font-medium">{c.agent_name}</span>
    },
    {
      key: 'carrier_name',
      header: 'Carrier',
      sortable: true
    },
    {
      key: 'policy_number',
      header: 'Policy #',
      sortable: true,
      render: (c) => <span className="font-mono text-sm">{c.policy_number}</span>
    },
    {
      key: 'gross_amount',
      header: 'Commission Total',
      sortable: true,
      className: 'text-right',
      render: (c) => formatCurrency(c.gross_amount)
    },
    {
      key: 'override_amount',
      header: 'My Override',
      sortable: true,
      className: 'text-right',
      render: (c) => (
        <div>
          <div className="font-semibold text-chestnut-gold">{formatCurrency(c.override_amount)}</div>
          {c.override_percentage && (
            <div className="text-xs text-chestnut-charcoal/60">{c.override_percentage}%</div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[c.status]}`}>
          {c.status}
        </span>
      )
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (c) => new Date(c.created_at).toLocaleDateString()
    }
  ]

  return (
    <AppLayout>
      <Head title="My Commissions" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">My Commissions</h1>
            <p className="text-chestnut-charcoal/70 mt-1">View your commission earnings and overrides from sponsored agents.</p>
          </div>
          <DateRangeFilter dateRange={date_range} path="/my/commissions" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-chestnut-forest/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-chestnut-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{formatCurrency(totals.direct)}</p>
                <p className="text-xs text-chestnut-charcoal/60">Direct Commissions{periodLabel}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{formatCurrency(totals.overrides)}</p>
                <p className="text-xs text-chestnut-charcoal/60">Override Earnings{periodLabel}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{formatCurrency(totals.combined)}</p>
                <p className="text-xs text-chestnut-charcoal/60">Total Earnings{periodLabel}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <PieChart className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totals.commission_count}</p>
                <p className="text-xs text-chestnut-charcoal/60">Commission Records{periodLabel}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray">
          <div className="border-b border-chestnut-gray">
            <div className="flex">
              <button
                onClick={() => setActiveTab('direct')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'direct'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <span className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  My Commissions ({my_commissions.length})
                </span>
              </button>
              <button
                onClick={() => setActiveTab('overrides')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overrides'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <span className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Override Earnings ({override_commissions.length})
                </span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'direct' ? (
              <DataTable
                data={my_commissions}
                columns={directColumns}
                searchPlaceholder="Search commissions..."
                searchKeys={['carrier_name', 'policy_number']}
                pageSize={15}
                emptyMessage="No commissions yet."
                getRowKey={(c) => c.id}
              />
            ) : (
              <DataTable
                data={override_commissions}
                columns={overrideColumns}
                searchPlaceholder="Search override commissions..."
                searchKeys={['agent_name', 'carrier_name', 'policy_number']}
                pageSize={15}
                emptyMessage="No override commissions from sponsored agents yet."
                getRowKey={(c) => c.id}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
